import type { RepoItem } from "@/components/features/matter/file-repo/FileRepository";
import { FolderTree } from "@/components/features/matter/file-repo/FolderTree";
import { Button, Typography } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { retrieveMatterFilesQueryOptions } from "@/lib/data/react-query/matters/matter-file";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type { MassTort, Matter } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { Folder, FolderPlus } from "lucide-react";
import { useLayoutEffect, useMemo, useState } from "react";

type SelectDestinationFolderModalBaseProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialFolderId?: string;
  onFolderSelected: (folderId: string) => void;
  title?: string;
  description?: string;
  overlayClassName?: string;
  className?: string;
};

type SelectDestinationFolderModalProps = SelectDestinationFolderModalBaseProps &
  (
    | {
        matter: Matter;
        massTort?: never;
      }
    | {
        massTort: MassTort;
        matter?: never;
      }
  );

export function SelectDestinationFolderModal({
  open,
  setOpen,
  matter,
  massTort,
  initialFolderId = "",
  onFolderSelected,
  title = "Destination folder",
  description = "Choose a destination folder.",
  overlayClassName,
  className,
}: SelectDestinationFolderModalProps) {
  const [currentFolderId, setCurrentFolderId] = useState(initialFolderId);
  const { openFileModal } = useGlobalModal();
  const massTortId = massTort?.id.trim() ?? "";
  const matterId = matter?.id.trim() ?? "";
  const { data: massTortResponse } = useQuery({
    ...retrieveMassTortQueryOptions(massTortId),
    enabled: open && Boolean(massTortId),
  });
  const { data: matterResponse } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: open && Boolean(matterId),
  });
  const { data: massTortFilesResponse } = useQuery({
    ...retrieveMatterFilesQueryOptions({ massTortId }),
    enabled: open && Boolean(massTortId),
  });
  const { data: matterFilesResponse } = useQuery({
    ...retrieveMatterFilesQueryOptions(matterId),
    enabled: open && Boolean(matterId),
  });
  const activeMassTort = massTortResponse?.data ?? massTort;
  const activeMatter = matterResponse?.data ?? matter;
  const matterFiles = useMemo(
    () =>
      matterFilesResponse?.data ??
      massTortFilesResponse?.data ??
      activeMatter?.files ??
      activeMassTort?.files ??
      [],
    [
      activeMassTort?.files,
      activeMatter?.files,
      massTortFilesResponse?.data,
      matterFilesResponse?.data,
    ],
  );
  const rootLabel = activeMatter ? "Matter Files" : "Mass Tort Files";
  const folderTreeMatter = useMemo(
    () => ({
      matterTitle:
        activeMatter?.title.trim() ||
        activeMassTort?.title?.trim() ||
        rootLabel,
    }),
    [activeMassTort?.title, activeMatter?.title, rootLabel],
  );
  const fileParentId = matterId || massTortId;
  const folderItems = useMemo(
    () =>
      matterFiles
        .filter((item) => item.isFolder)
        .map(
          (item): RepoItem =>
            ({
              ...item,
              matterId: item.matterId ?? fileParentId,
              kind: "regular",
            }) as RepoItem,
        ),
    [fileParentId, matterFiles],
  );
  const breadcrumbs = useMemo(() => {
    const crumbs: RepoItem[] = [];
    let parentId = currentFolderId;

    while (parentId) {
      const folder = folderItems.find((item) => item.id === parentId);
      if (!folder) break;
      crumbs.unshift(folder);
      parentId = folder.idLocation ?? "";
    }

    return crumbs;
  }, [currentFolderId, folderItems]);
  const childFolders = useMemo(
    () =>
      folderItems
        .filter((folder) => (folder.idLocation || "") === currentFolderId)
        .sort((folderA, folderB) => folderA.name.localeCompare(folderB.name)),
    [currentFolderId, folderItems],
  );

  useLayoutEffect(() => {
    if (open) {
      setCurrentFolderId(initialFolderId);
    }
  }, [initialFolderId, open]);

  const handleCreateFolder = () => {
    openFileModal({
      ...(activeMatter
        ? { matterId: activeMatter.id }
        : { massTortId: activeMassTort?.id ?? "" }),
      editing: false,
      currentFolderId,
      isFolder: true,
      showPreview: false,
      afterCreateFile: (file) => {
        setCurrentFolderId(file.id);
      },
    });
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      overlayClassName={overlayClassName}
      className={className}
      size="3xl"
      bodyContained
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <FormCancelButton setOpen={setOpen} />
          <Button
            type="button"
            onClick={() => {
              onFolderSelected(currentFolderId);
              setOpen(false);
            }}
          >
            Select folder
          </Button>
        </div>
      }
    >
      <div className="flex min-h-[min(55vh,520px)] overflow-hidden">
        <div className="border-border w-80 shrink-0 overflow-auto border-r p-4">
          <FolderTree
            matter={folderTreeMatter}
            items={folderItems}
            onSelectItem={setCurrentFolderId}
            selectedItemId={currentFolderId || null}
            onMoveItem={() => false}
            enableDrag={false}
            currentFolderId={currentFolderId}
            showCheckboxes={false}
            showHeader={false}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-4">
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              className="text-primary"
              onClick={() => {
                setCurrentFolderId("");
              }}
            >
              {rootLabel}
            </button>
            {breadcrumbs.map((folder) => (
              <div key={folder.id} className="flex items-center gap-2">
                <span className="text-muted-foreground">/</span>
                <button
                  type="button"
                  className="text-primary"
                  onClick={() => {
                    setCurrentFolderId(folder.id);
                  }}
                >
                  {folder.name}
                </button>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="mb-4 w-full shrink-0 justify-start gap-2"
            onClick={handleCreateFolder}
          >
            <FolderPlus className="h-4 w-4" />
            Create new folder
          </Button>

          {childFolders.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
              {childFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className="hover:bg-primary/5 flex items-center gap-3 rounded-md border p-3 text-left"
                  onClick={() => {
                    setCurrentFolderId(folder.id);
                  }}
                >
                  <Folder className="h-5 w-5 text-amber-500" />
                  <span className="truncate">{folder.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <Typography className="text-muted-foreground text-sm">
                No subfolders here.
              </Typography>
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
}
